<script>
import axios from 'axios'

const WINDOW_OPTIONS = [
  { value: 24, text: 'Last 24 hours' },
  { value: 72, text: 'Last 3 days' },
  { value: 168, text: 'Last 7 days' },
  { value: 720, text: 'Last 30 days' }
]

export default {
  props: {
    device: {
      type: String,
      default: ''
    },
    baseUrl: {
      type: String,
      default: 'http://localhost:5000'
    },
    basePath: {
      type: String,
      default: '/monitoring/firewalls'
    }
  },
  data () {
    return {
      detail: {},
      hours: 168,
      windowOptions: WINDOW_OPTIONS,
      isBusy: false,
      loadError: '',
      showRawPayload: false
    }
  },
  computed: {
    uptime () {
      return this.detail.uptime || {}
    },
    ha () {
      return this.detail.ha || {}
    },
    // Session synchronisation over HA2, which is what decides whether a
    // failover keeps established connections. It is not on the flattened HA
    // document, only on the raw payload the firewall returned.
    stateSync () {
      const raw = this.detail.ha_raw || {}
      const local = (raw.group || {})['local-info'] || {}
      return local['state-sync'] || null
    },
    uptimeSeries () {
      const history = this.detail.uptime_history || []
      return [{
        name: 'Uptime (days)',
        data: history
          .filter((point) => typeof point.uptime === 'number')
          .map((point) => [Date.parse(point.ts), +(point.uptime / 86400).toFixed(2)])
      }]
    },
    uptimeOptions () {
      const reboots = this.detail.reboots || []
      return {
        chart: { type: 'area', toolbar: { show: false } },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#727cf5'],
        fill: { opacity: 0.2 },
        // ApexCharts renders datetime axes in UTC unless told otherwise.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
        yaxis: {
          labels: { formatter: (value) => `${Math.round(value)}d` },
          min: 0
        },
        annotations: {
          xaxis: reboots.map((reboot) => ({
            x: Date.parse(reboot.detected_at),
            borderColor: '#fa5c7c',
            label: {
              style: { color: '#fff', background: '#fa5c7c' },
              text: 'reboot'
            }
          }))
        },
        tooltip: { x: { format: 'dd MMM HH:mm' } }
      }
    },
    hasHistory () {
      return (this.detail.uptime_history || []).length > 1
    },
    rawPayload () {
      return JSON.stringify(this.detail.ha_raw || {}, null, 2)
    }
  },
  watch: {
    device () {
      this.loadDetail()
    },
    hours () {
      this.loadDetail()
    }
  },
  mounted () {
    this.loadDetail()
  },
  methods: {
    loadDetail () {
      if (!this.device) return
      this.isBusy = true
      this.loadError = ''
      axios
        .get(`${this.baseUrl}${this.basePath}/device/${encodeURIComponent(this.device)}?hours=${this.hours}`)
        .then((response) => {
          this.detail = typeof response.data === 'string' ? JSON.parse(response.data) : response.data
        })
        .catch((error) => {
          this.loadError = `Could not load device detail: ${error.message}`
        })
        .finally(() => {
          this.isBusy = false
        })
    },
    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString('en-CO', {
        timeZone: 'America/Bogota',
        hour12: false
      })
    },
    stateVariant (state) {
      if (state === 'active' || state === 'standalone') return 'success'
      if (state === 'passive') return 'info'
      if (!state) return 'secondary'
      return 'danger'
    }
  }
}
</script>

<template>
  <div>
    <b-alert
      :show="loadError.length > 0"
      variant="danger"
      dismissible
      @dismissed="loadError = ''"
    >
      {{ loadError }}
    </b-alert>

    <div
      v-if="isBusy"
      class="text-center py-4"
    >
      <b-spinner class="align-middle" />
      <strong>&nbsp;&nbsp;Loading...</strong>
    </div>

    <div v-else>
      <!-- Facts -->
      <div class="row">
        <div class="col-md-8">
          <table class="table table-sm table-borderless mb-2">
            <tbody>
              <tr>
                <th class="text-muted">
                  Hostname
                </th>
                <td>{{ uptime.hostname || device }}</td>
                <th class="text-muted">
                  Serial
                </th>
                <td>{{ uptime.serial || '—' }}</td>
              </tr>
              <tr>
                <th class="text-muted">
                  Model
                </th>
                <td>{{ uptime.model || '—' }}</td>
                <th class="text-muted">
                  Version
                </th>
                <td>{{ uptime.sw_version || '—' }}</td>
              </tr>
              <tr>
                <th class="text-muted">
                  IP
                </th>
                <td>{{ uptime.ip || '—' }}</td>
                <th class="text-muted">
                  Last seen
                </th>
                <td>{{ formatDate(uptime.ts) }}</td>
              </tr>
              <tr>
                <th class="text-muted">
                  Uptime
                </th>
                <td>{{ uptime.uptime_text || '—' }}</td>
                <th class="text-muted">
                  Poll time
                </th>
                <td>{{ uptime.duration_ms ? `${uptime.duration_ms} ms` : '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="col-md-4">
          <div class="card bg-light mb-2">
            <div class="card-body py-2">
              <h5 class="text-muted font-size-13 mb-1">
                HA role
              </h5>
              <div class="d-flex align-items-baseline mb-2">
                <b-badge
                  :variant="stateVariant(ha.local_state)"
                  class="font-size-13"
                >
                  {{ ha.local_state || 'unknown' }}
                </b-badge>
                <span
                  v-if="ha.mode"
                  class="text-muted font-size-13 ml-2"
                >{{ ha.mode }}</span>
              </div>
              <dl
                v-if="ha.ha_enabled"
                class="font-size-12 mb-0 ha-facts"
              >
                <dt>Peer</dt>
                <dd>
                  <b-badge
                    :variant="stateVariant(ha.peer_state)"
                    class="font-size-12"
                  >
                    {{ ha.peer_state || '—' }}
                  </b-badge>
                </dd>
                <dt>Peer link</dt>
                <dd>
                  {{ ha.peer_conn_status || '—' }}
                </dd>
                <template v-if="stateSync">
                  <dt>Session sync</dt>
                  <dd>
                    {{ stateSync }}
                  </dd>
                </template>
                <dt>Config sync</dt>
                <dd class="text-muted">
                  {{ ha.running_sync || '—' }}
                </dd>
                <template v-if="ha.local_state_since">
                  <dt>In role since</dt>
                  <dd>
                    {{ formatDate(ha.local_state_since) }}
                  </dd>
                </template>
              </dl>
              <div
                v-else
                class="font-size-12 text-muted"
              >
                Standalone device (no HA peer configured)
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Uptime history -->
      <div class="d-flex align-items-center mb-1">
        <h5 class="font-size-14 mb-0 mr-2">
          Uptime history
        </h5>
        <b-form-select
          v-model="hours"
          size="sm"
          :options="windowOptions"
          style="max-width: 160px;"
        />
      </div>
      <apexchart
        v-if="hasHistory"
        type="area"
        height="220"
        :series="uptimeSeries"
        :options="uptimeOptions"
      />
      <div
        v-else
        class="text-muted py-3 text-center"
      >
        Not enough history in this window yet (samples arrive every 15 minutes).
      </div>

      <div class="row mt-2">
        <!-- Reboots -->
        <div class="col-md-6">
          <h5 class="font-size-14">
            Reboots detected
          </h5>
          <ul
            v-if="(detail.reboots || []).length"
            class="list-unstyled font-size-13"
          >
            <li
              v-for="(reboot, index) in detail.reboots"
              :key="`reboot-${index}`"
            >
              <feather
                type="rotate-ccw"
                class="icon-xs text-danger mr-1"
              />
              Rebooted at ≈ {{ formatDate(reboot.rebooted_at) }}
            </li>
          </ul>
          <div
            v-else
            class="text-muted font-size-13"
          >
            No reboots detected in this window.
          </div>
        </div>
        <!-- HA transitions -->
        <div class="col-md-6">
          <h5 class="font-size-14">
            HA state changes
          </h5>
          <ul
            v-if="(detail.ha_transitions || []).length"
            class="list-unstyled font-size-13"
          >
            <li
              v-for="(transition, index) in detail.ha_transitions"
              :key="`transition-${index}`"
            >
              <feather
                type="repeat"
                class="icon-xs text-warning mr-1"
              />
              {{ formatDate(transition.ts) }}:
              <b-badge :variant="stateVariant(transition.from_state)">
                {{ transition.from_state }}
              </b-badge>
              →
              <b-badge :variant="stateVariant(transition.to_state)">
                {{ transition.to_state }}
              </b-badge>
            </li>
          </ul>
          <div
            v-else
            class="text-muted font-size-13"
          >
            No HA state changes in this window.
          </div>
        </div>
      </div>

      <!-- Collector errors -->
      <div
        v-if="(detail.errors || []).length"
        class="mt-2"
      >
        <h5 class="font-size-14">
          Recent collector errors (7 days)
        </h5>
        <div class="table-responsive">
          <table class="table table-sm font-size-12">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Meaning</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(error, index) in detail.errors"
                :key="`error-${index}`"
              >
                <td class="text-nowrap">
                  {{ formatDate(error.ts) }}
                </td>
                <td>
                  <b-badge variant="secondary">
                    {{ error.error_type }}
                  </b-badge>
                </td>
                <td>{{ error.meaning }}</td>
                <td
                  class="text-truncate"
                  style="max-width: 320px;"
                >
                  {{ error.error }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Raw HA payload -->
      <div class="mt-2">
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="showRawPayload = !showRawPayload"
        >
          {{ showRawPayload ? 'Hide' : 'Show' }} raw HA payload
        </b-button>
        <pre
          v-if="showRawPayload"
          class="bg-light p-2 mt-2 font-size-11"
          style="max-height: min(320px, 35vh); overflow: auto;"
        >{{ rawPayload }}</pre>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Label and value in two aligned columns rather than four sentences running
   into each other, and one size for every fact. The state badge and the mode
   used to inherit an h4, which put them at 16px and 15.7px against 12px body
   text for no reason anyone reading the panel could infer. */
.ha-facts {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 12px;
  row-gap: 5px;
  align-items: baseline;
}

.ha-facts dt {
  font-weight: 400;
  color: #98a2b3;
  white-space: nowrap;
}

.ha-facts dd {
  margin: 0;
}
</style>
